// Pure render-path resolver for the global Layer Properties console.
//
// Mirrors the runtime routing decisions so the Render Path tab can show the
// *expected* adapter + render path for ANY layer kind — even before the layer
// mounts, and even for kinds the current scene does not contain. It is kept
// pure (no engine imports, no I/O) and layer-KIND aware, never layer-id aware.
//
// Sources of truth this mirrors (kept in sync intentionally):
//   - `viewer-sdk/policy/engineRouting.ts`         (kind → default engine)
//   - `viewer-sdk/ViewerHost.pickPointCloudAdapter` (pointcloud sub-routing)
//   - `engines/giro3d/giro3dRuntime.describeGiro3DLayer` (map-layer vs object)
//   - `modules/weather/catalog.isEnvironmentLayer`  (environment detection)
//
// When the runtime changes routing, update this resolver + its tests together.

import type { EngineId, Layer } from '@geosoft/viewer-sdk';
import type { RenderPathKind } from '@/engines/contract/diagnostics';

const COPC_RE = /\.copc(?:\.laz)?(?:[?#].*)?$/i;
const POTREE_METADATA_RE = /(?:^|\/)metadata\.json(?:[?#].*)?$/i;

/** Twin-mode default: Giro3D primary with a MapLibre vector overlay. */
const DEFAULT_AVAILABLE_ENGINES: readonly EngineId[] = ['giro3d', 'maplibre'];

export interface ResolveRenderPathOptions {
  readonly primaryEngine?: EngineId;
  readonly availableEngines?: readonly EngineId[];
}

export interface ResolvedRenderPath {
  readonly adapter: EngineId | null;
  readonly path: RenderPathKind;
}

export function isCopcEndpoint(url: string | undefined | null): boolean {
  return typeof url === 'string' && COPC_RE.test(url);
}

export function isPotreeMetadataEndpoint(url: string | undefined | null): boolean {
  return typeof url === 'string' && POTREE_METADATA_RE.test(url);
}

/** Human-facing label for a render path (Overview + Render Path tabs). */
export function renderPathLabel(path: RenderPathKind): string {
  switch (path) {
    case 'giro3d-map-layer':
      return 'Giro3D map layer (ColorLayer / ElevationLayer)';
    case 'giro3d-native-vector':
      return 'Giro3D native vector layer (PMTiles / MVT)';
    case 'giro3d-instance-object':
      return 'Giro3D instance object (3D Tiles / glTF / point cloud)';
    case 'maplibre-overlay':
      return 'MapLibre overlay (vector / raster)';
    case 'environment-controller':
      return 'Environment controller (scene effects)';
    case 'cesium-primitive':
      return 'Cesium primitive';
    case 'potree-pointcloud':
      return 'Potree point cloud';
    case 'three-object':
      return 'Three.js object';
    case 'react-overlay':
      return 'React overlay (outside engine host)';
    case 'panel-only':
      return 'Panel only (not a map/3D layer)';
    case 'unknown':
    default:
      return 'Unknown';
  }
}

function isEnvironmentLayer(layer: Layer): boolean {
  if (layer.kind === 'environment') return true;
  if (layer.id.startsWith('layer:environment:')) return true;
  const family = (layer.style as Record<string, unknown> | undefined)?.['family'];
  return family === 'environment';
}

function resolvePointCloudPath(
  layer: Layer,
  available: readonly EngineId[],
): ResolvedRenderPath {
  const has = (id: EngineId) => available.includes(id);
  // 1. 3D Tiles point cloud → Giro3D preferred, Cesium fallback.
  if (layer.endpoints.tileset3d) {
    if (has('giro3d')) return { adapter: 'giro3d', path: 'giro3d-instance-object' };
    if (has('cesium')) return { adapter: 'cesium', path: 'cesium-primitive' };
    return { adapter: null, path: 'giro3d-instance-object' };
  }
  // 2. COPC → Giro3D.
  const copc =
    layer.endpoints.copc ??
    (isCopcEndpoint(layer.endpoints.pointcloud) ? layer.endpoints.pointcloud : undefined);
  if (copc) {
    if (has('giro3d')) return { adapter: 'giro3d', path: 'giro3d-instance-object' };
    if (has('cesium')) return { adapter: 'cesium', path: 'cesium-primitive' };
    return { adapter: null, path: 'giro3d-instance-object' };
  }
  // 3. Potree metadata → Potree adapter if mounted, else Giro3D's PotreeSource path.
  const potree =
    layer.endpoints.potree ??
    (isPotreeMetadataEndpoint(layer.endpoints.pointcloud) ? layer.endpoints.pointcloud : undefined);
  if (potree) {
    if (has('potree')) return { adapter: 'potree', path: 'potree-pointcloud' };
    if (has('giro3d')) return { adapter: 'giro3d', path: 'giro3d-instance-object' };
    return { adapter: 'potree', path: 'potree-pointcloud' };
  }
  // 4. Unknown point-cloud source → Giro3D COPC path attempt.
  if (has('giro3d')) return { adapter: 'giro3d', path: 'giro3d-instance-object' };
  return { adapter: null, path: 'unknown' };
}

/**
 * Resolve the expected adapter + render path for `layer`, mirroring runtime
 * routing. Defaults to Twin mode (Giro3D primary + MapLibre overlay).
 */
export function resolveExpectedRenderPath(
  layer: Layer,
  options: ResolveRenderPathOptions = {},
): ResolvedRenderPath {
  const primaryEngine = options.primaryEngine ?? 'giro3d';
  const available = options.availableEngines ?? DEFAULT_AVAILABLE_ENGINES;
  const has = (id: EngineId) => available.includes(id);

  if (isEnvironmentLayer(layer)) {
    return { adapter: 'giro3d', path: 'environment-controller' };
  }

  switch (layer.kind) {
    case 'panorama-bubble':
      return { adapter: null, path: 'react-overlay' };
    case 'timeseries':
      return { adapter: null, path: 'panel-only' };
    case 'sensor':
      return { adapter: has('maplibre') ? 'maplibre' : null, path: 'maplibre-overlay' };
    case 'vector':
      if (primaryEngine === 'giro3d' && layer.endpoints.pmtiles && has('giro3d')) {
        return { adapter: 'giro3d', path: 'giro3d-native-vector' };
      }
      return { adapter: has('maplibre') ? 'maplibre' : null, path: 'maplibre-overlay' };
    case 'panorama':
      return { adapter: has('maplibre') ? 'maplibre' : null, path: 'maplibre-overlay' };
    case 'raster':
      if (primaryEngine === 'giro3d' && has('giro3d')) {
        return { adapter: 'giro3d', path: 'giro3d-map-layer' };
      }
      return { adapter: has('maplibre') ? 'maplibre' : null, path: 'maplibre-overlay' };
    case 'terrain': {
      // quantized-mesh terrain is overridden to Cesium in the routing policy.
      const intakeKind = (layer.style as Record<string, unknown> | undefined)?.['intakeKind'];
      if (intakeKind === 'quantized-mesh' && has('cesium')) {
        return { adapter: 'cesium', path: 'cesium-primitive' };
      }
      if (has('giro3d')) return { adapter: 'giro3d', path: 'giro3d-map-layer' };
      if (has('cesium')) return { adapter: 'cesium', path: 'cesium-primitive' };
      return { adapter: null, path: 'giro3d-map-layer' };
    }
    case '3dtiles':
      if (has('giro3d')) return { adapter: 'giro3d', path: 'giro3d-instance-object' };
      if (has('cesium')) return { adapter: 'cesium', path: 'cesium-primitive' };
      return { adapter: null, path: 'giro3d-instance-object' };
    case 'bim':
      if (primaryEngine === 'giro3d' && has('giro3d')) {
        return { adapter: 'giro3d', path: 'giro3d-instance-object' };
      }
      if (has('three')) return { adapter: 'three', path: 'three-object' };
      if (has('giro3d')) return { adapter: 'giro3d', path: 'giro3d-instance-object' };
      return { adapter: null, path: 'three-object' };
    case 'pointcloud':
      return resolvePointCloudPath(layer, available);
    default:
      return { adapter: null, path: 'unknown' };
  }
}

const RENDER_PATH_VALUES: ReadonlySet<RenderPathKind> = new Set<RenderPathKind>([
  'giro3d-map-layer',
  'giro3d-native-vector',
  'giro3d-instance-object',
  'maplibre-overlay',
  'environment-controller',
  'cesium-primitive',
  'potree-pointcloud',
  'three-object',
  'react-overlay',
  'panel-only',
  'unknown',
]);

/** Coerce a live engine `renderPath` string into the typed union (or null). */
export function coerceRenderPath(value: string | undefined | null): RenderPathKind | null {
  if (typeof value !== 'string') return null;
  return RENDER_PATH_VALUES.has(value as RenderPathKind) ? (value as RenderPathKind) : null;
}
